import {
  Approver,
  ShiftEventAndUpdateEvent,
  StoppageKind,
  isPartialStoppage,
  isStoppageTypeForNewWithCodeOldWithReason,
  isStoppageTypeWithCode,
  isStoppageTypeWithReason
} from '@domain/shifthandover'
import {FormStoppageTypeWithDepartments, Iso8601, TimeRange} from '@hconnect/common/types'
import {shouldNotBeEmpty, type Validator} from '@hconnect/common/validators'
import {isEmpty} from 'lodash'
import moment from 'moment'

export const shouldNotBeEmptyArray: Validator<ShiftEventAndUpdateEvent> = (
  value: string[] = []
) => {
  return value.length > 0 ? undefined : 'shouldBe.notEmpty'
}

export const requiredEquipmentNumberValidator: Validator<ShiftEventAndUpdateEvent> = (
  value: string,
  item
) => {
  return item.eventType !== StoppageKind.PlannedStoppage && !item.equipment
    ? 'shouldBe.notEmpty'
    : undefined
}

const formStoppageTypeWithDepartmentsValues: readonly string[] = Object.values(
  FormStoppageTypeWithDepartments
)
export const requiredStoppedMainEquipmentNumberValidator: Validator<ShiftEventAndUpdateEvent> = (
  value: string,
  item
) => {
  return isPartialStoppage(item) &&
    item.stoppageType &&
    formStoppageTypeWithDepartmentsValues.includes(item.stoppageType) &&
    !item.upmDepartmentId
    ? 'shouldBe.notEmpty'
    : undefined
}

export const requiredMainEquipmentNumberValidator: Validator<ShiftEventAndUpdateEvent> = (
  value: string,
  item
) => {
  return item.eventType === StoppageKind.PlannedStoppage && !item.mainEquipment
    ? 'shouldBe.notEmpty'
    : undefined
}

const stoppageValidatorWithCodeOldWithReason = (item: Partial<ShiftEventAndUpdateEvent>) => {
  const value = item['stoppageCode'] || item['stoppageReason']
  return value && value.trim().length > 0 ? undefined : 'shouldBe.notEmpty'
}

export const stoppageReasonValidator: Validator<ShiftEventAndUpdateEvent> = (
  value: string,
  item: Partial<ShiftEventAndUpdateEvent>
) => {
  if (
    isPartialStoppage(item) &&
    item.stoppageType &&
    isStoppageTypeForNewWithCodeOldWithReason(item.stoppageType)
  ) {
    return stoppageValidatorWithCodeOldWithReason(item)
  }
  if (isPartialStoppage(item) && item.stoppageType && isStoppageTypeWithReason(item.stoppageType)) {
    return value && value.trim().length > 0 ? undefined : 'shouldBe.notEmpty'
  }
}

export const stoppageCodeValidator: Validator<ShiftEventAndUpdateEvent> = (
  value: string,
  item: Partial<ShiftEventAndUpdateEvent>
) => {
  if (
    isPartialStoppage(item) &&
    item.stoppageType &&
    isStoppageTypeForNewWithCodeOldWithReason(item.stoppageType)
  ) {
    return stoppageValidatorWithCodeOldWithReason(item)
  }
  if (isPartialStoppage(item) && item.stoppageType && isStoppageTypeWithCode(item.stoppageType)) {
    return value && value.trim().length > 0 ? undefined : 'shouldBe.notEmpty'
  }
}

export const validFromValidator: Validator<ShiftEventAndUpdateEvent> = (
  date: Iso8601 | undefined,
  item: Partial<ShiftEventAndUpdateEvent>
) => {
  if (date && item.eventType !== StoppageKind.PlannedStoppage && moment(date).isAfter(moment())) {
    return 'shouldBe.notInFutureDate'
  }
  return shouldNotBeEmpty<ShiftEventAndUpdateEvent>()(date, item)
}

export const validToValidator: Validator<ShiftEventAndUpdateEvent> = (
  date: Iso8601 | undefined,
  item: Partial<ShiftEventAndUpdateEvent>
) => {
  if (date && item.eventType !== StoppageKind.PlannedStoppage && moment(date).isAfter(moment())) {
    return 'shouldBe.notInFutureDate'
  }
  if (date && 'stoppageStart' in item && !moment(date).isAfter(item.stoppageStart)) {
    return 'shouldBe.dateAfter'
  }
  return undefined
}

export const commonFormScheduleEndValidator: Validator<ShiftEventAndUpdateEvent> = (
  date: TimeRange | undefined,
  item: Partial<ShiftEventAndUpdateEvent>
) => {
  if ('schedule' in item && item.schedule) {
    if (date?.endDate) {
      return moment(date.endDate).isAfter(item.schedule.startDate)
        ? undefined
        : 'shouldBe.dateAfter'
    }
    return 'shouldBe.notEmpty'
  }
  return undefined
}

export const approversValidator: Validator<ShiftEventAndUpdateEvent> = (
  value: Approver[],
  item: Partial<ShiftEventAndUpdateEvent>
) => {
  if ('needsApproval' in item && item.needsApproval && isEmpty(value)) {
    return 'shouldBe.notEmpty'
  }
}
